// ── Modules ────────────────────────────────────────────────────────────────────

export type ModuleId = 'reports' | 'competitor' | 'context' | 'brief-builder' | 'faq'

export interface ModuleMeta {
  id: ModuleId
  label: string
  tagline: string
  icon: string          // emoji fallback
  color: string         // tailwind colour class for accent
  apiBase: string       // e.g. /api/reports
}

export const MODULES: ModuleMeta[] = [
  {
    id: 'reports',
    label: 'Report Generator',
    tagline: 'Turn any research brief into a sourced, formatted intelligence report.',
    icon: '📊',
    color: 'blue',
    apiBase: '/api/reports',
  },
  {
    id: 'competitor',
    label: 'Competitor Intelligence',
    tagline: 'Crawl, extract and compare competitor features automatically.',
    icon: '🔍',
    color: 'violet',
    apiBase: '/api/competitor',
  },
  {
    id: 'context',
    label: 'Context Builder',
    tagline: 'Generate vector-store-ready context documents for any company.',
    icon: '📁',
    color: 'emerald',
    apiBase: '/api/context',
  },
  {
    id: 'brief-builder',
    label: 'Brief Builder',
    tagline: 'Convert client briefs into validated tenant configuration YAML with AI assistance.',
    icon: '📋',
    color: 'indigo',
    apiBase: '/api/brief-builder',
  },
  {
    id: 'faq',
    label: 'FAQ Chatbot',
    tagline: 'Ask anything — answers grounded in your knowledge base via OpenAI vector search.',
    icon: '💬',
    color: 'teal',
    apiBase: '/api/faq',
  },
]

// ── Generic job types (shared by all modules) ─────────────────────────────────

export type JobState = 'idle' | 'queued' | 'running' | 'complete' | 'failed'

export interface BaseJobStatus {
  job_id: string
  status: JobState
  step?: string
  progress: number
  created_at: string
  completed_at?: string
  error?: string
  label?: string
}

/** One entry returned by GET /api/{module}/jobs (history list). */
export type HistoryJob = BaseJobStatus

// ── Compare Reports ───────────────────────────────────────────────────────────

export interface CompareDiscrepancy {
  topic: string
  uploaded_says: string
  generated_says: string
}

export interface CompareResult {
  source_filename: string
  report_title: string
  summary: string
  in_uploaded_only: string[]
  in_generated_only: string[]
  discrepancies: CompareDiscrepancy[]
  verdict: string
  processing_time_s: number
}

// ── Report Generator ──────────────────────────────────────────────────────────

export interface ReportJobStatus extends BaseJobStatus {
  files?: { html: boolean; excel: boolean }
  title?: string
  question_count?: number
  warnings?: string[]
}

export interface ReportOptions {
  pages?: number
  tone: 'professional' | 'executive' | 'analytical'
  audience: string
  gl: string
  skip_research: boolean
  plausible: boolean
  evidence: boolean
  // Mode
  mode: 'research' | 'tenant'
  tenant?: string
  // Data source (research mode)
  data_source: 'serp' | 'tavily' | 'both'
  // Time constraint (research mode)
  date_from?: string   // YYYY-MM
  date_to?: string     // YYYY-MM
}

export const DEFAULT_REPORT_OPTIONS: ReportOptions = {
  tone: 'professional',
  audience: '',
  gl: 'us',
  skip_research: false,
  plausible: false,
  evidence: false,
  mode: 'research',
  tenant: undefined,
  data_source: 'serp',
  date_from: `${new Date().getFullYear() - 1}-01`,
  date_to:   `${new Date().getFullYear() - 1}-12`,
}

// ── Competitor ────────────────────────────────────────────────────────────────

export interface CompetitorPreset {
  name: string
  label: string
  description: string
  url_count: number
}

export interface CompetitorOptions {
  preset?: string
  urls_content?: string
  features_yaml?: string
  comparison: boolean
  input_company: string
  concurrency?: number
}

export const DEFAULT_COMPETITOR_OPTIONS: CompetitorOptions = {
  comparison: false,
  input_company: '',
}

export interface CompetitorJobStatus extends BaseJobStatus {
  files?: { markdown: boolean; excel: boolean }
  result_count?: number
}

// ── Context Builder ───────────────────────────────────────────────────────────

export interface ContextPreset {
  name: string
  label: string
  description: string
  company: string
  product: string
}

export interface ContextOptions {
  preset?: string
  config_yaml?: string
  company?: string
  product?: string
  department?: string
  challenges?: string[]
}

export const DEFAULT_CONTEXT_OPTIONS: ContextOptions = {}

export interface ContextJobStatus extends BaseJobStatus {
  files?: { zip: boolean; doc_count: number }
  doc_names?: string[]
}

// ── UI state machines ─────────────────────────────────────────────────────────

export type AppView =
  | { view: 'home' }
  | { view: 'module'; module: ModuleId }

export type JobView =
  | { view: 'input' }
  | { view: 'loading'; jobId: string; step: string; progress: number }
  | { view: 'result'; jobId: string; meta?: Record<string, unknown> }
  | { view: 'error';  message: string }

// ── Pipeline steps per module ─────────────────────────────────────────────────

export interface PipelineStep { id: string; label: string; pct: number }

export const REPORT_STEPS: PipelineStep[] = [
  { id: 'parsing',     label: 'Parsing brief',    pct: 5  },
  { id: 'researching', label: 'Researching data', pct: 20 },
  { id: 'analysing',   label: 'Analysing evidence',pct: 45 },
  { id: 'visualizing', label: 'Creating charts',  pct: 62 },
  { id: 'writing',     label: 'Writing report',   pct: 76 },
  { id: 'validating',  label: 'Validating claims',pct: 89 },
  { id: 'formatting',  label: 'Formatting',       pct: 95 },
]

export const COMPETITOR_STEPS: PipelineStep[] = [
  { id: 'crawling',   label: 'Crawling websites',   pct: 5  },
  { id: 'extracting', label: 'Extracting features', pct: 35 },
  { id: 'validating', label: 'Validating results',  pct: 65 },
  { id: 'comparing',  label: 'Comparing companies', pct: 80 },
  { id: 'reporting',  label: 'Generating report',   pct: 92 },
]

export const CONTEXT_STEPS: PipelineStep[] = [
  { id: 'configuring', label: 'Loading config',       pct: 5  },
  { id: 'describing',  label: 'Company description',  pct: 20 },
  { id: 'market',      label: 'Market reports',       pct: 40 },
  { id: 'consumer',    label: 'Consumer reports',     pct: 58 },
  { id: 'competitor',  label: 'Competitor reports',   pct: 72 },
  { id: 'department',  label: 'Department brief',     pct: 84 },
  { id: 'challenges',  label: 'Recommendations',      pct: 93 },
  { id: 'packaging',   label: 'Packaging files',      pct: 97 },
]
